import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { useMyGoals } from '$tablet-app/src/app/goals/useGoals/useGoals';
import { MainDrawerScreenProps } from '$tablet-app/src/app/navigation/MainDrawerNavigator';
import { SectionIntroScreen } from '$tablet-app/src/app/screens/SectionIntroScreen';
import { useCurrentUserID } from '$tablet-app/src/app/users';
import { InfoVideoName } from '$tablet-app/src/app/videos/types';
import { GoalPeriod } from '@akin-home/amplify';
import { Button } from '@akin-home/buttons/native';
import { Goal, filterGoalsByPeriod } from '@akin-home/goals';
import { useGetSystemProperty } from '@akin-home/system-properties-react';
import { useIsFocused } from '@react-navigation/native';
import produce from 'immer';
import React, { FC, useEffect, useMemo, useReducer } from 'react';
import { useIncrementGoal } from '../../hooks/useIncrementGoal';
import YourGoalsScreenView from './YourGoalsScreenView';

export type YourGoalsScreenAction =
  | {
      type: 'CHANGE_DAY';
      payload: { day: Date };
    }
  | { type: 'SET_TYPE_FILTER'; payload: { filter: GoalTypeFilter } };

export type GoalTypeFilter = 'all' | 'day' | 'week' | 'month';

interface State {
  day: Date;
  type_filter: GoalTypeFilter;
}

const reducer = produce((state: State, action: YourGoalsScreenAction) => {
  switch (action.type) {
    case 'CHANGE_DAY':
      state.day = action.payload.day;
      break;
    case 'SET_TYPE_FILTER':
      state.type_filter = action.payload.filter;
  }
});

const YourGoalsScreen: FC<MainDrawerScreenProps<'Goals'>> = ({
  navigation: { navigate },
}) => {
  const [state, dispatch] = useReducer(reducer, {
    day: new Date(),
    type_filter: 'all',
  });

  const user_id = useCurrentUserID();

  const is_focused = useIsFocused();

  const { data: info_video } = useGetSystemProperty(InfoVideoName.GOALS_SCREEN);

  useUpdateScreenChatContext();

  const { data: all_goals, isLoading } = useMyGoals();

  const goals = useMemo(() => {
    switch (state.type_filter) {
      case 'all':
        return all_goals;
      case 'day':
        return filterGoalsByPeriod(GoalPeriod.day, all_goals);
      case 'week':
        return filterGoalsByPeriod(GoalPeriod.week, all_goals);
      case 'month':
        return filterGoalsByPeriod(GoalPeriod.month, all_goals);
      default:
        return all_goals;
    }
  }, [all_goals, state.type_filter]);

  const onIncrementGoalCompletion = useIncrementGoal({
    date: state.day,
    user_id,
  });

  const onDecrementGoalCompletion = (goal: Goal) =>
    onIncrementGoalCompletion(goal, -1);

  const onChangeDay = (day: Date) =>
    dispatch({ type: 'CHANGE_DAY', payload: { day } });

  const onPressAddGoal = () => navigate('CreateGoal');

  const onPressEditGoal = ({ id }: Goal) => navigate('EditGoal', { id });

  const onPressStatistics = () => navigate('GoalStatistics');

  const onChangeTypeFilter = (filter: GoalTypeFilter) =>
    dispatch({ type: 'SET_TYPE_FILTER', payload: { filter } });

  useEffect(() => {
    if (is_focused && !isLoading) {
      sendBackgroundMessage(intent_labels.open_goals_screen, {
        context: { goal_count: all_goals?.length },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, is_focused]);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!isLoading && !all_goals?.length && info_video?.value?.length) {
    return (
      <SectionIntroScreen
        screen_title="Goals"
        video_title="Watch the video and learn how to create your first goal"
        source={{ drive_id: info_video.value }}
        action_button={
          <Button icon="plus" onPress={onPressAddGoal}>
            Add your first goal
          </Button>
        }
      />
    );
  }

  return (
    <>
      <YourGoalsScreenView
        day={state.day}
        user_id={user_id}
        goals={goals}
        type_filter={state.type_filter}
        onPressGoal={onIncrementGoalCompletion}
        onLongPressGoal={onDecrementGoalCompletion}
        onPressEditGoal={onPressEditGoal}
        onChangeDay={onChangeDay}
        onPressCreateGoal={onPressAddGoal}
        onPressStatistics={onPressStatistics}
        onChangeTypeFilter={onChangeTypeFilter}
      />

      <DeveloperScreenInfo />
    </>
  );
};

export default YourGoalsScreen;
